<template>
  <div class="qr-overlay" @click.self="$emit('close')">
    <div class="qr-modal">
      <div class="qr-header">
        <h3>Escanear QR</h3>
        <button class="close-btn" @click="$emit('close')">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="qr-body">
        <!-- Camera View -->
        <div v-show="state === 'scanning'" class="camera-container">
          <qrcode-stream @detect="onDetect" @error="onCameraError" />
          <p class="scan-hint">Apunta la cámara al código QR</p>
        </div>

        <!-- Loading State -->
        <div v-if="state === 'loading'" class="status-panel">
          <span class="material-icons spin">sync</span>
          <p>Consultando información...</p>
        </div>

        <!-- Camera Error -->
        <div v-if="state === 'camera-error'" class="status-panel error">
          <span class="material-icons">videocam_off</span>
          <p>{{ errorMessage }}</p>
          <button class="primary" @click="resetScanner">Reintentar</button>
        </div>

        <!-- Error State -->
        <div v-if="state === 'error'" class="status-panel error">
          <span class="material-icons">error_outline</span>
          <p>{{ errorMessage }}</p>
          <button class="primary" @click="resetScanner">Escanear otro</button>
        </div>

        <!-- Confirmation Panel -->
        <div v-if="state === 'confirm'" class="confirm-panel">
          <div class="confirm-header">
            <span class="material-icons success-icon">check_circle</span>
            <h4>Bombero identificado</h4>
          </div>
          <div class="confirm-details">
            <div class="detail-row">
              <span class="detail-label">Nombre</span>
              <span class="detail-value">{{ scannedPerson.name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Compañía</span>
              <span class="detail-value">{{ scannedPerson.station }}</span>
            </div>
          </div>
          <div class="confirm-actions">
            <button class="primary full-width" @click="confirmAdd">
              Agregar
            </button>
            <button class="secondary full-width" @click="resetScanner">
              Descartar
            </button>
          </div>
        </div>

        <!-- Success feedback (brief) -->
        <div v-if="state === 'added'" class="status-panel success">
          <span class="material-icons">person_add</span>
          <p>{{ lastAddedName }} agregado/a</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader'

const ICBS_URL_PATTERN = /^https?:\/\/icbs\.cl\/b\/[\w-]+$/

function capitalizeWords(text) {
  if (!text) return ''
  return text
    .toLowerCase()
    .split(' ')
    .filter((w) => w.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function parseIcbsHtml(html) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const body = doc.querySelector('.box-body')
  if (!body) return null

  const text = body.innerHTML
  const nombres = extractField(text, 'NOMBRES')
  const apellidos = extractField(text, 'APELLIDOS')
  const compania = extractField(text, 'COMPAÑÍA')

  if (!nombres && !apellidos) return null

  return {
    name: capitalizeWords(
      [nombres, apellidos].filter(Boolean).join(' ').trim()
    ),
    station: compania ? compania.trim() : '',
  }
}

function extractField(html, fieldName) {
  const regex = new RegExp(`<b>${fieldName}</b><br>([^<]+)`, 'i')
  const match = html.match(regex)
  return match ? match[1].trim() : null
}

export default {
  components: { QrcodeStream },
  emits: ['close', 'person-scanned'],
  data() {
    return {
      state: 'scanning', // scanning | loading | confirm | error | camera-error | added
      errorMessage: '',
      scannedPerson: { name: '', station: '' },
      lastAddedName: '',
    }
  },
  methods: {
    async onDetect(detectedCodes) {
      if (this.state !== 'scanning' || !detectedCodes.length) return

      const rawValue = detectedCodes[0].rawValue
      if (!rawValue) return

      // Plain text mode: NEXO|nombre|apellidos|compania
      if (rawValue.startsWith('NEXO|')) {
        this.handlePlainText(rawValue)
        return
      }

      // URL mode: check if it looks like a URL
      if (rawValue.startsWith('http')) {
        if (ICBS_URL_PATTERN.test(rawValue)) {
          await this.handleIcbsUrl(rawValue)
        } else {
          this.state = 'error'
          this.errorMessage =
            'QR no válido. No corresponde a una credencial de bombero.'
        }
        return
      }

      // Anything else is invalid
      this.state = 'error'
      this.errorMessage =
        'QR no válido. No corresponde a una credencial de bombero.'
    },

    handlePlainText(value) {
      const parts = value.split('|')
      // Expected: NEXO|nombre|apellidos|compania
      if (parts.length < 4) {
        this.state = 'error'
        this.errorMessage =
          'QR con formato incorrecto. Se esperaba: NEXO|Nombre|Apellidos|Compañía'
        return
      }

      const nombre = parts[1].trim()
      const apellidos = parts[2].trim()
      const compania = parts[3].trim()

      if (!nombre && !apellidos) {
        this.state = 'error'
        this.errorMessage = 'El QR no contiene un nombre válido.'
        return
      }

      this.scannedPerson = {
        name: capitalizeWords([nombre, apellidos].filter(Boolean).join(' ')),
        station: compania,
      }
      this.state = 'confirm'
    },

    async handleIcbsUrl(url) {
      this.state = 'loading'
      try {
        const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(url)
        const response = await fetch(proxyUrl)
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        const html = await response.text()
        const person = parseIcbsHtml(html)

        if (!person) {
          this.state = 'error'
          this.errorMessage =
            'No se pudo leer la información del bombero desde la página.'
          return
        }

        this.scannedPerson = person
        this.state = 'confirm'
      } catch {
        this.state = 'error'
        this.errorMessage =
          'No se pudo conectar. Verifica tu conexión a internet.'
      }
    },

    onCameraError(error) {
      this.state = 'camera-error'
      if (error.name === 'NotAllowedError') {
        this.errorMessage =
          'Permiso de cámara denegado. Habilita el acceso a la cámara en la configuración del navegador.'
      } else if (error.name === 'NotFoundError') {
        this.errorMessage = 'No se encontró una cámara en este dispositivo.'
      } else {
        this.errorMessage = `Error al acceder a la cámara: ${error.message}`
      }
    },

    confirmAdd() {
      this.$emit('person-scanned', { ...this.scannedPerson })
      this.lastAddedName = this.scannedPerson.name
      this.state = 'added'
      // Return to scanning after brief feedback
      setTimeout(() => {
        this.resetScanner()
      }, 1500)
    },

    resetScanner() {
      this.state = 'scanning'
      this.errorMessage = ''
      this.scannedPerson = { name: '', station: '' }
    },
  },
}
</script>

<style scoped>
.qr-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.qr-modal {
  background-color: #2f3136;
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #40444b;
}

.qr-header h3 {
  margin: 0;
  color: #ffffff;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  color: #b9bbbe;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: #40444b;
  color: #ffffff;
}

.qr-body {
  padding: 20px;
  overflow-y: auto;
}

.camera-container {
  border-radius: 8px;
  overflow: hidden;
  background-color: #202225;
}

.scan-hint {
  text-align: center;
  color: #b9bbbe;
  font-size: 14px;
  margin: 12px 0 0 0;
}

.status-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 16px;
  text-align: center;
}

.status-panel .material-icons {
  font-size: 48px;
  color: #b9bbbe;
}

.status-panel.error .material-icons {
  color: #ed4245;
}

.status-panel.success .material-icons {
  color: #2dce89;
}

.status-panel p {
  color: #dcddde;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.confirm-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.confirm-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.confirm-header h4 {
  margin: 0;
  color: #ffffff;
  font-size: 16px;
}

.success-icon {
  color: #2dce89;
  font-size: 24px;
}

.confirm-details {
  background-color: #202225;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  color: #72767d;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.detail-value {
  color: #ffffff;
  font-size: 16px;
}

.confirm-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

button {
  border-radius: 4px;
  border: none;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
}

button.primary {
  background-color: #2dce89;
  color: white;
}

button.primary:hover {
  background-color: #1d9863;
}

button.secondary {
  background-color: #4f545c;
  color: #dcddde;
}

button.secondary:hover {
  background-color: #5c626b;
}

button.full-width {
  width: 100%;
}
</style>
